import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, MapPin, Users } from "lucide-react";

import { listMyMeetupEvents, type MyMeetupEvent } from "@/lib/api/participants";
import { useAuthUser } from "@/lib/use-auth-user";

import { Loader, ErrorBlock } from "@/components/ui/States";
import { DashboardTopbar } from "@/components/layout/DashboardTopbar";
import { DashboardSidebar } from "@/components/layout/DashboardSidebar";

const ROLE_LABEL: Record<string, string> = {
  manager: "Organizer",
  recruiter: "Recruiter",
  candidate: "Candidate",
  participant: "Participant",
  admin: "Admin",
};

function shortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay - startOfToday) / (24 * 3600 * 1000));
  if (dayDiff === 0) return "Today";
  if (dayDiff === 1) return "Tomorrow";
  if (dayDiff === -1) return "Yesterday";
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}

function fullDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fullDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function diffToHm(targetMs: number, nowMs: number): { hours: number; minutes: number } | null {
  const ms = targetMs - nowMs;
  if (ms <= 0) return null;
  const minutes = Math.floor(ms / 60000);
  return { hours: Math.floor(minutes / 60), minutes: minutes % 60 };
}

function eventBadge(m: MyMeetupEvent, now: number): { label: string; tone: "blue" | "green" | "grey" } {
  const evMs = new Date(m.event_date).getTime();
  if (m.event_status === "cancelled") return { label: "Cancelled", tone: "grey" };
  if (m.event_status === "completed" || evMs < now - 12 * 3600 * 1000) {
    return { label: "Completed", tone: "grey" };
  }
  if (evMs < now) return { label: "Live", tone: "green" };
  return { label: "Upcoming", tone: "blue" };
}

function pitchTone(m: MyMeetupEvent): "ready" | "missing" | "none" {
  if (m.has_video && (m.pitch_status === "approved" || m.pitch_status === "submitted")) return "ready";
  const evMs = new Date(m.event_date).getTime();
  if (Date.now() > evMs + 12 * 3600 * 1000 && !m.has_video) return "none";
  return "missing";
}

export function MyMeetupsPage() {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useAuthUser();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const { data: meetups, isLoading, error } = useQuery({
    queryKey: ["my-meetup-events", user?.id],
    queryFn: listMyMeetupEvents,
    enabled: Boolean(user),
  });

  const upcoming = useMemo(
    () =>
      (meetups ?? []).filter((m) => {
        const evMs = new Date(m.event_date).getTime();
        return m.event_status === "active" && evMs > now - 12 * 3600 * 1000;
      }),
    [meetups, now],
  );

  const past = useMemo(
    () =>
      (meetups ?? []).filter((m) => {
        const evMs = new Date(m.event_date).getTime();
        return (
          m.event_status === "completed" ||
          m.event_status === "cancelled" ||
          (m.event_status === "active" && evMs < now - 12 * 3600 * 1000)
        );
      }),
    [meetups, now],
  );

  const totalMatches = useMemo(
    () => (meetups ?? []).reduce((s, m) => s + (m.my_match_count || 0), 0),
    [meetups],
  );

  const banner = useMemo(() => {
    for (const m of upcoming) {
      if (!(m.pitch_status === "approved" || m.pitch_status === "submitted")) continue;
      const evMs = new Date(m.event_date).getTime();
      const revealMs = evMs - (m.match_reveal_hours || 2) * 3600 * 1000;
      if (revealMs > now && evMs > now) {
        const remaining = diffToHm(revealMs, now);
        if (!remaining) continue;
        return { meetup: m, remaining, revealMs };
      }
    }
    return null;
  }, [upcoming, now]);

  const onCardAction = (m: MyMeetupEvent) => {
    const evMs = new Date(m.event_date).getTime();
    const isPast =
      m.event_status === "completed" ||
      m.event_status === "cancelled" ||
      evMs < now - 12 * 3600 * 1000;
    if (isPast) {
      if (m.my_match_count > 0) navigate(`/e/${m.slug}/matches`);
      else navigate(`/e/${m.slug}/wrap`);
      return;
    }
    if (!m.has_video) navigate(`/e/${m.slug}/pitch`);
    else navigate(`/e/${m.slug}/feed`);
  };

  if (userLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#F0F2F5]">
        <Loader label="Loading..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <DashboardTopbar />

      <div className="md:grid md:min-h-[calc(100vh-64px)] md:grid-cols-[260px_1fr_300px]">
        <DashboardSidebar active="events" />

        <main className="min-w-0 px-5 py-8 pb-24 md:px-10 md:py-10 md:pb-10">
          <header className="mb-8">
            <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">My events</h1>
            <p className="mt-1 text-[14px] text-[#6c757d]">
              All meetups you're registered for
            </p>
          </header>

          {isLoading ? (
            <Loader label="Loading events..." />
          ) : error ? (
            <ErrorBlock
              title="Couldn't load events"
              message={error instanceof Error ? error.message : String(error)}
            />
          ) : (
            <>
              {banner ? (
                <RevealBanner
                  title={banner.meetup.title}
                  location={banner.meetup.location}
                  date={banner.meetup.event_date}
                  remaining={banner.remaining}
                  onOpen={() => navigate(`/e/${banner.meetup.slug}/feed`)}
                />
              ) : null}

              <SectionHeader title="Upcoming" />
              {upcoming.length === 0 ? (
                <EmptyBlock text="No upcoming events. Get an invite to register." />
              ) : (
                <div className="mb-10 grid gap-5 grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
                  {upcoming.map((m) => (
                    <EventCard key={m.event_id} m={m} now={now} onAction={() => onCardAction(m)} />
                  ))}
                </div>
              )}

              <SectionHeader title="Past" />
              {past.length === 0 ? (
                <EmptyBlock text="No past events yet." />
              ) : (
                <div className="mb-10 grid gap-5 grid-cols-[repeat(auto-fill,minmax(320px,1fr))]">
                  {past.map((m) => (
                    <EventCard key={m.event_id} m={m} now={now} onAction={() => onCardAction(m)} />
                  ))}
                </div>
              )}
            </>
          )}
        </main>

        <ProfileWidget
          name={user?.fullName || "—"}
          email={user?.email || ""}
          avatarUrl={user?.avatarUrl ?? null}
          activeRole={user?.activeRole ?? null}
          totalEvents={(meetups ?? []).length}
          totalMatches={totalMatches}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfileWidget (right column, desktop only)
// ---------------------------------------------------------------------------

function ProfileWidget({
  name,
  email,
  avatarUrl,
  activeRole,
  totalEvents,
  totalMatches,
}: {
  name: string;
  email: string;
  avatarUrl: string | null;
  activeRole: string | null;
  totalEvents: number;
  totalMatches: number;
}) {
  const roleText = activeRole ? ROLE_LABEL[activeRole] || activeRole : "Participant";
  return (
    <aside className="hidden border-l border-[#e2e8f0] bg-white px-6 py-8 md:block">
      <div className="rounded-[20px] border border-[#e2e8f0] bg-white p-6 text-center shadow-[0_2px_16px_rgba(0,0,0,0.04)]">
        <div className="mb-3 flex justify-center">
          <div className="h-[72px] w-[72px] overflow-hidden rounded-full border-[3px] border-[#e2e8f0] bg-gradient-to-br from-[#dbeafe] to-[#bfdbfe]">
            {avatarUrl ? <img src={avatarUrl} alt="" className="h-full w-full object-cover" /> : null}
          </div>
        </div>
        <p className="text-[16px] font-bold text-[#1a1a1a]">{name}</p>
        <p className="mt-0.5 truncate text-[13px] text-[#6c757d]">{email || roleText}</p>

        <div className="mt-4 flex border-t border-[#e2e8f0] pt-4">
          <div className="flex-1 text-center">
            <p className="text-[20px] font-bold tracking-tight text-[#1a1a1a]">{totalEvents}</p>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
              Events
            </p>
          </div>
          <div className="flex-1 border-l border-[#e2e8f0] text-center">
            <p className="text-[20px] font-bold tracking-tight text-[#1a1a1a]">{totalMatches}</p>
            <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
              Matches
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Reveal banner
// ---------------------------------------------------------------------------

function RevealBanner({
  title,
  location,
  date,
  remaining,
  onOpen,
}: {
  title: string;
  location: string | null;
  date: string;
  remaining: { hours: number; minutes: number };
  onOpen: () => void;
}) {
  return (
    <div className="mb-8 flex flex-col items-start justify-between gap-4 rounded-[20px] bg-gradient-to-br from-[#1e3a8a] to-[#2563eb] px-6 py-6 text-white sm:flex-row sm:items-center sm:px-8">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide opacity-70">Coming up</p>
        <p className="mt-1.5 text-[20px] font-bold tracking-tight">
          Matches reveal in {remaining.hours}h {remaining.minutes}m
        </p>
        <p className="mt-1 text-[13px] opacity-75">
          {title}
          {location ? ` — ${location}` : ""}, {fullDateTime(date)}
        </p>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="rounded-[12px] bg-white px-6 py-3 text-[14px] font-bold text-[#2563eb] transition hover:opacity-90"
      >
        Open matches
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header / empty
// ---------------------------------------------------------------------------

function SectionHeader({ title, link }: { title: string; link?: { to: string; label: string } }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-[16px] font-bold text-[#1a1a1a]">{title}</h2>
      {link ? (
        <Link to={link.to} className="text-[13px] font-semibold text-[#2563eb] hover:underline">
          {link.label}
        </Link>
      ) : null}
    </div>
  );
}

function EmptyBlock({ text }: { text: string }) {
  return (
    <div className="mb-10 rounded-[20px] border border-dashed border-[#e2e8f0] bg-white px-6 py-12 text-center text-[13px] text-[#94a3b8]">
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventCard
// ---------------------------------------------------------------------------

function EventCard({ m, now, onAction }: { m: MyMeetupEvent; now: number; onAction: () => void }) {
  const badge = eventBadge(m, now);
  const tone = pitchTone(m);
  const evMs = new Date(m.event_date).getTime();
  const isPast =
    m.event_status === "completed" ||
    m.event_status === "cancelled" ||
    evMs < now - 12 * 3600 * 1000;
  const revealMs = evMs - (m.match_reveal_hours || 2) * 3600 * 1000;
  const remaining = diffToHm(revealMs, now);

  let info: { tone: "amber" | "green"; label: string; text: string } | null = null;
  if (!isPast) {
    if (tone === "ready" && remaining) {
      info = {
        tone: "green",
        label: `Matches in ${remaining.hours}:${String(remaining.minutes).padStart(2, "0")}`,
        text: "The match list opens before the event",
      };
    } else if (tone === "missing") {
      info = {
        tone: "amber",
        label: "Pitch needed",
        text: "Record a video — matchmaking won't run without it",
      };
    }
  } else if (m.my_match_count > 0) {
    info = {
      tone: "green",
      label: `${m.my_match_count} ${m.my_match_count === 1 ? "match" : "matches"}`,
      text: "Both sides confirmed",
    };
  }

  let footerLabel = "";
  let footerColor = "";
  let dotClass = "";
  if (tone === "ready") {
    dotClass = "bg-[#10b981]";
    footerLabel = isPast ? "Pitch was recorded" : "Pitch ready";
    footerColor = isPast ? "text-[#6c757d]" : "text-[#065f46]";
  } else if (tone === "missing") {
    dotClass = "bg-[#f59e0b]";
    footerLabel = "Pitch missing";
    footerColor = "text-[#92400e]";
  } else {
    dotClass = "bg-[#e2e8f0]";
    footerLabel = "No pitch recorded";
    footerColor = "text-[#94a3b8]";
  }

  let buttonLabel = "Open";
  let buttonStyle = "bg-[#2563eb] text-white hover:bg-[#1d4ed8]";
  if (isPast) {
    if (m.my_match_count > 0) buttonLabel = "Matches";
    else buttonLabel = "Details";
    buttonStyle = "bg-white text-[#1a1a1a] border border-[#e2e8f0] hover:bg-[#f8fafc]";
  } else if (tone === "missing") {
    buttonLabel = "Record pitch";
  }

  return (
    <button
      type="button"
      onClick={onAction}
      className="flex flex-col overflow-hidden rounded-[20px] border border-[#e2e8f0] bg-white text-left shadow-[0_2px_16px_rgba(0,0,0,0.04)] transition hover:-translate-y-0.5 hover:shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
    >
      <div className="px-6 pt-6">
        <div className="mb-3.5 flex items-center justify-between">
          <Badge tone={badge.tone}>{badge.label}</Badge>
          <span className="text-[12px] text-[#94a3b8]">{shortDate(m.event_date)}</span>
        </div>
        <h3 className="mb-1.5 text-[18px] font-bold leading-tight tracking-tight text-[#1a1a1a]">
          {m.title}
        </h3>
        <p className="mb-4 text-[13px] text-[#6c757d]">
          {m.role_at_event && m.company_at_event
            ? `${m.role_at_event} · ${m.company_at_event}`
            : m.role_at_event || m.company_at_event || ""}
        </p>
        <div className="mb-4 flex flex-col gap-1.5 text-[13px] text-[#475569]">
          <div className="flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-[#94a3b8]" />
            <span>{fullDate(m.event_date)}</span>
          </div>
          {m.location ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-[#94a3b8]" />
              <span>{m.location}</span>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5 text-[#94a3b8]" />
            <span>{m.participant_count} {m.participant_count === 1 ? "participant" : "participants"}</span>
          </div>
        </div>
      </div>

      {info ? (
        info.tone === "amber" ? (
          <div className="mx-6 mb-4 rounded-r-[6px] border-l-[3px] border-[#f59e0b] bg-[#fffbeb] px-3.5 py-2.5 text-[12px] text-[#78350f]">
            <p className="text-[11px] font-bold uppercase tracking-wide">{info.label}</p>
            <p className="mt-0.5">{info.text}</p>
          </div>
        ) : (
          <div className="mx-6 mb-4 rounded-[8px] border border-[#a7f3d0] bg-[#f0fdf4] px-3.5 py-2.5 text-[12px] text-[#065f46]">
            <p className="text-[11px] font-bold uppercase tracking-wide">{info.label}</p>
            <p className="mt-0.5">{info.text}</p>
          </div>
        )
      ) : (
        <div className="h-4" />
      )}

      <div className="mt-auto flex items-center justify-between border-t border-[#e2e8f0] px-6 py-4">
        <div className="flex items-center gap-2 text-[13px] font-medium">
          <span className={`h-2 w-2 rounded-full ${dotClass}`} />
          <span className={footerColor}>{footerLabel}</span>
        </div>
        <span
          className={`rounded-[12px] px-4 py-2 text-[13px] font-semibold ${buttonStyle}`}
          onClick={(e) => {
            e.stopPropagation();
            onAction();
          }}
        >
          {buttonLabel}
        </span>
      </div>
    </button>
  );
}

function Badge({ tone, children }: { tone: "blue" | "green" | "grey"; children: React.ReactNode }) {
  const styles =
    tone === "green"
      ? "bg-[#f0fdf4] text-[#065f46]"
      : tone === "grey"
        ? "bg-[#f8fafc] text-[#94a3b8]"
        : "bg-[#eff6ff] text-[#2563eb]";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[6px] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${styles}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "green" ? "animate-pulse bg-[#10b981]" : "bg-current"}`} />
      {children}
    </span>
  );
}
